import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import * as Adflow from 'adflow';
import { Box, Button, ButtonGroup, HStack, IconButton, Stack, Text } from '@chakra-ui/react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  type SoccerFixture,
  type SoccerPreviewQualification,
  getFixtureLabel,
  getFixturesForStage,
  resolveStage,
} from 'adflow';

type ViewMode = 'generic' | 'soccer';

const PREVIEW_HEADER_TOP_PADDING = 4;
const VIEW_TOGGLE_SLOT_HEIGHT = '32px';
const FIXTURE_SWITCHER_SLOT_HEIGHT = '52px';

type Props = Adflow.RenderPreviewArgs;

const renderGenericPreview = (
  args: Adflow.RenderPreviewArgs,
  activeSoccerFixture?: SoccerFixture,
  resolveSoccerCustomAssetUrls?: boolean,
): ReactElement =>
  args.importSourceType === 'creatomate' && args.importSourceJSON ? (
    <Adflow.CreatomatePreview
      source={args.importSourceJSON}
      elements={args.elements}
      brand={args.brand}
      displayOptions={args.displayOptions}
    />
  ) : (
    <Adflow.Preview
      elements={args.elements}
      brand={args.brand}
      brandKit={args.brandKit}
      fonts={args.fonts}
      displayOptions={args.displayOptions}
      selectedElementId={args.selectedElementId}
      isIndexPreview={false}
      dataSources={args.dataSources}
      activeSoccerFixture={activeSoccerFixture}
      resolveSoccerCustomAssetUrls={resolveSoccerCustomAssetUrls}
      animation={args.animation}
      animationPlaybackToken={args.animationPlaybackToken}
    />
  );

const TemplateBuilderPreview = (props: Props): ReactElement => {
  const isCreatomate = props.importSourceType === 'creatomate' && Boolean(props.importSourceJSON);

  const qualification = useMemo<SoccerPreviewQualification>(
    () => (isCreatomate ? { kind: 'not-soccer' } : resolveStage(props.dataSources)),
    [isCreatomate, props.dataSources],
  );

  if (qualification.kind === 'not-soccer') {
    return <PreviewLayout>{renderGenericPreview(props)}</PreviewLayout>;
  }

  return <SoccerEligiblePreview qualification={qualification} args={props} />;
};

type SoccerEligiblePreviewProps = {
  qualification: Exclude<SoccerPreviewQualification, { kind: 'not-soccer' }>;
  args: Adflow.RenderPreviewArgs;
};

const SoccerEligiblePreview = ({
  qualification,
  args,
}: SoccerEligiblePreviewProps): ReactElement => {
  const [view, setView] = useState<ViewMode>('generic');
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);
  const [fixtureActivatedAt, setFixtureActivatedAt] = useState(() => new Date());

  const stage = qualification.kind === 'valid' ? qualification.stage : null;
  const fixtures = useMemo(
    () => (stage ? getFixturesForStage(stage, fixtureActivatedAt) : []),
    [stage, fixtureActivatedAt],
  );

  useEffect(() => {
    if (!stage) {
      setSelectedFixtureId(null);
      return;
    }

    const nextActivation = new Date();
    setFixtureActivatedAt(nextActivation);
    setSelectedFixtureId(getFixturesForStage(stage, nextActivation)[0]?.id ?? null);
  }, [stage]);

  const activeIndex = Math.max(
    0,
    fixtures.findIndex((fixture) => fixture.id === selectedFixtureId),
  );
  const activeFixture = fixtures[activeIndex];

  const selectFixture = (fixtureId: string) => {
    setSelectedFixtureId(fixtureId);
    setFixtureActivatedAt(new Date());
  };

  const showFixturePaginator = view === 'soccer' && qualification.kind === 'valid' && activeFixture;

  return (
    <PreviewLayout
      toggleControls={<ViewToggle view={view} onChange={setView} />}
      fixtureControls={
        showFixturePaginator ? (
          <FixturePaginator
            fixtures={fixtures}
            activeIndex={activeIndex}
            activeFixture={activeFixture}
            onPrev={() => selectFixture(fixtures[activeIndex - 1]?.id ?? activeFixture.id)}
            onNext={() => selectFixture(fixtures[activeIndex + 1]?.id ?? activeFixture.id)}
          />
        ) : null
      }
    >
      {view === 'generic' ? (
        renderGenericPreview(args, undefined, false)
      ) : qualification.kind === 'invalid' ? (
        <InvalidStageWarning reason={qualification.reason} />
      ) : (
        <SoccerPreviewView activeFixture={activeFixture} args={args} />
      )}
    </PreviewLayout>
  );
};

type ViewToggleProps = {
  view: ViewMode;
  onChange: (next: ViewMode) => void;
};

const ViewToggle = ({ view, onChange }: ViewToggleProps): ReactElement => (
  <ButtonGroup size="sm" isAttached variant="outline" data-testid="template-preview-view-toggle">
    <Button
      isActive={view === 'generic'}
      onClick={() => onChange('generic')}
      data-testid="template-preview-view-generic"
    >
      Design
    </Button>
    <Button
      isActive={view === 'soccer'}
      onClick={() => onChange('soccer')}
      data-testid="template-preview-view-soccer"
    >
      Preview
    </Button>
  </ButtonGroup>
);

type SoccerPreviewViewProps = {
  activeFixture?: SoccerFixture;
  args: Adflow.RenderPreviewArgs;
};

const SoccerPreviewView = ({ activeFixture, args }: SoccerPreviewViewProps): ReactElement => {
  if (!activeFixture) {
    return <FixtureLibraryWarning />;
  }

  return (
    <Stack spacing={4} align="center" maxWidth="100%">
      {renderGenericPreview(args, activeFixture, true)}
    </Stack>
  );
};

type FixturePaginatorProps = {
  fixtures: ReadonlyArray<SoccerFixture>;
  activeIndex: number;
  activeFixture: SoccerFixture;
  onPrev: () => void;
  onNext: () => void;
};

const FixturePaginator = ({
  fixtures,
  activeIndex,
  activeFixture,
  onPrev,
  onNext,
}: FixturePaginatorProps): ReactElement => (
  <HStack
    spacing={3}
    align="center"
    data-testid="soccer-fixture-paginator"
    aria-label="Soccer fixtures"
  >
    <IconButton
      aria-label="Previous fixture"
      icon={<ChevronLeftIcon fontSize="small" />}
      size="sm"
      variant="ghost"
      onClick={onPrev}
      isDisabled={activeIndex <= 0}
      data-testid="soccer-fixture-prev"
    />
    <Box minWidth="280px" textAlign="center">
      <Text fontSize="sm" fontWeight="semibold" data-testid="soccer-fixture-label">
        {getFixtureLabel(activeFixture)}
      </Text>
      <Text fontSize="xs" color="gray.500">
        {activeIndex + 1} / {fixtures.length}
      </Text>
    </Box>
    <IconButton
      aria-label="Next fixture"
      icon={<ChevronRightIcon fontSize="small" />}
      size="sm"
      variant="ghost"
      onClick={onNext}
      isDisabled={activeIndex >= fixtures.length - 1}
      data-testid="soccer-fixture-next"
    />
  </HStack>
);

const FixtureSwitcherSlot = ({ children }: { children?: ReactNode }): ReactElement => (
  <Box
    minH={FIXTURE_SWITCHER_SLOT_HEIGHT}
    display="flex"
    alignItems="center"
    justifyContent="center"
    width="100%"
  >
    {children}
  </Box>
);

const ViewToggleSlot = ({ children }: { children?: ReactNode }): ReactElement => (
  <Box
    minH={VIEW_TOGGLE_SLOT_HEIGHT}
    display="flex"
    alignItems="center"
    justifyContent="center"
    width="100%"
  >
    {children}
  </Box>
);

const PreviewLayout = ({
  children,
  toggleControls,
  fixtureControls,
}: {
  children: ReactNode;
  toggleControls?: ReactNode;
  fixtureControls?: ReactNode;
}): ReactElement => (
  <Stack spacing={4} align="center" maxWidth="100%" pt={PREVIEW_HEADER_TOP_PADDING}>
    <ViewToggleSlot>{toggleControls}</ViewToggleSlot>
    <FixtureSwitcherSlot>{fixtureControls}</FixtureSwitcherSlot>
    {children}
  </Stack>
);

const InvalidStageWarning = ({
  reason,
}: {
  reason: 'no-stage' | 'multiple-stages';
}): ReactElement => (
  <Box
    role="alert"
    data-testid="soccer-preview-invalid-stage"
    px={3}
    py={2}
    borderLeft="4px solid"
    borderColor="orange.400"
    backgroundColor="orange.50"
    color="orange.800"
    fontSize="sm"
    width="100%"
    maxWidth="480px"
  >
    {reason === 'no-stage'
      ? 'Add a Soccer stage data source (e.g. SOCCER_PRE) to preview Soccer fixtures.'
      : 'This template binds multiple Soccer stages. Soccer preview supports exactly one stage at a time.'}
  </Box>
);

const FixtureLibraryWarning = (): ReactElement => (
  <Box
    role="alert"
    data-testid="soccer-preview-fixtures-missing"
    px={3}
    py={2}
    borderLeft="4px solid"
    borderColor="orange.400"
    backgroundColor="orange.50"
    color="orange.800"
    fontSize="sm"
    width="100%"
    maxWidth="480px"
  >
    No mock fixtures are available for this Soccer stage.
  </Box>
);

export default TemplateBuilderPreview;
